import { NgIf } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AssetDto, GalleryAccessLevel, GalleryDto } from '@picflow/types';
import { firstValueFrom } from 'rxjs';
import { UploadObj } from 'src/app/classes/upload-obj.class';
import { GallerySettingsDialogComponent } from 'src/app/gallery/dialogs/gallery-settings-dialog/gallery-settings-dialog.component';
import { changeCoverAsset } from 'src/app/lib/change-cover-asset';
import { fileToBuffer } from 'src/app/lib/file-to-buffer';
import { fitImageIntoAspectRatio } from 'src/app/lib/get-and-resize-image-file';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { AssetService } from 'src/app/services/asset.service';
import { BrowserService } from 'src/app/services/browser.service';
import { DialogService } from 'src/app/services/dialog.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { GalleryService } from 'src/app/services/gallery.service';
import { GuestAuthService } from 'src/app/services/guest-auth.service';
import { PermissionService } from 'src/app/services/permission.service';
import { StateService } from 'src/app/services/state.service';
import { ToastService } from 'src/app/services/toast.service';
import { UploadService } from 'src/app/services/upload.service';
import { GuestProfileDialogComponent } from 'src/app/view-gallery/dialogs/guest-profile-dialog/guest-profile-dialog.component';
import { ProTagComponent } from '../../../shared-extra/components/pro-tag/pro-tag.component';
import { PermissionsPipe } from '../../../shared-extra/pipes/permissions.pipe';
import { ButtonMenuItemComponent } from '../../../shared/components/button-menu-item/button-menu-item.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { MoveAssetsDialogComponent } from '../../dialogs/move-assets-dialog/move-assets-dialog.component';

@Component({
  selector: 'app-asset-context-menu',
  templateUrl: './asset-context-menu.component.html',
  styleUrls: ['./asset-context-menu.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    ButtonDirective,
    SvgComponent,
    ButtonMenuItemComponent,
    ProTagComponent,
    PermissionsPipe,
    TranslatePipe,
  ],
})
export class AssetContextMenuComponent implements OnChanges {
  @ViewChild('versionUpload') versionUploadEl: ElementRef;
  @ViewChild('posterUpload') posterUploadEl: ElementRef;
  @Input() asset: AssetDto;
  @Input() gallery: GalleryDto;
  @Input() accessLevel: GalleryAccessLevel;
  @Input() isGalleryGuest: boolean;
  @Input() clientMode: boolean;
  @Input() assetPageMode: boolean;
  @Input() thumbnailsShown: boolean;
  @Output() onClose = new EventEmitter();
  @Output() onDelete = new EventEmitter();
  @Output() onCoverChange = new EventEmitter();
  @Output() openShare = new EventEmitter();
  @Output() onNewVersion = new EventEmitter();
  @Output() onToggleThumbnails = new EventEmitter();
  @Output() onMoved = new EventEmitter();
  @Output() onCopied = new EventEmitter();

  url: string;

  constructor(
    private assetService: AssetService,
    private dialog: DialogService,
    private galleryService: GalleryService,
    private toasts: ToastService,
    private uploadService: UploadService,
    public featureFlags: FeatureFlagsService,
    private analytics: AnalyticsService,
    public state: StateService,
    private browserService: BrowserService,
    private guestAuth: GuestAuthService,
    private permissionService: PermissionService,
    private router: Router
  ) {}

  ngOnChanges(): void {
    this.url = window.location.href;
  }

  downloadAsset() {
    this.assetService.downloadAssetVersion(
      this.asset.version_data,
      this.asset.type,
      this.gallery.id,
      this.asset.watermark_enabled
    );
  }

  async setAsCover() {
    changeCoverAsset(this.gallery.cover, this.asset);
    this.gallery.cover.position = '50% 50%';
    const resp = await this.galleryService.updateGallery(this.gallery.id, {
      cover: this.gallery.cover,
      teaser: this.gallery.cover,
    });
    this.gallery.cover = resp.cover;
    this.toasts.add('Cover updated', 'success');
    this.onCoverChange.emit();
    this.onClose.emit();
  }

  async deleteAsset() {
    this.onDelete.emit();
    this.onClose.emit();
    await this.assetService.deleteAsset(this.asset, this.isGalleryGuest);
    this.analytics.track('asset_deleted', true);
  }

  async uploadVersion(_files: File[]) {
    const files = Array.from(_files);
    if (!files.length) return;
    if (files.length > 1) return this.toasts.add('Please upload only one asset', 'error');
    const file = files[0];
    const resp = this.uploadService.handleCanUpload(files);
    if (!resp) return;

    await this.uploadService.uploadNewVersion(this.asset, file);
    await this.uploadService.waitForAssetProcessing(this.asset.id);
    this.onNewVersion.emit();
    this.onClose.emit();
  }

  uploadAssetEvent(target: EventTarget) {
    const files = (target as HTMLInputElement).files;
    this.uploadVersion(Array.from(files));
    this.versionUploadEl.nativeElement.value = '';
  }

  async openGuestProfileDialog() {
    if (this.state.user) {
      location.href = this.browserService.getCorrectPicflowUrl() + '/a/settings/profile';
      return;
    }
    if (!this.state.guestAuthToken) {
      await this.guestAuth.authenticateWithPopup(this.gallery);
    } else {
      this.dialog.open(GuestProfileDialogComponent, { gallery: this.gallery, accessLevel: this.accessLevel });
    }
  }

  async moveAsset() {
    const resp = await this.dialog.open(MoveAssetsDialogComponent, { assets: [this.asset], gallery: this.gallery });
    if (resp === 'moved') {
      this.onMoved.emit();
    }
  }

  async toggleWatermark() {
    this.asset.watermark_enabled = !this.asset.watermark_enabled;
    this.assetService.updateAsset(
      this.asset,
      { watermark_enabled: this.asset.watermark_enabled },
      this.gallery.id,
      <string>this.asset.section
    );
  }

  openWatermarkingSettings() {
    if (this.permissionService.canDisplayElement('asset.watermarking', this.gallery)) {
      return this.dialog.open(GallerySettingsDialogComponent, { gallery: this.gallery });
    }
    const tenantId = this.gallery['tenant']?.id || this.gallery['tenant'];
    if (this.permissionService.shouldRedirectToPricingPage(tenantId)) {
      this.router.navigate(['/a', 'pricing']);
    }
  }

  logout() {
    this.guestAuth.logout();
    this.toasts.add('You are now logged out.', 'success');
  }

  async uploadCustomPoster(target: EventTarget) {
    if (!this.permissionService.canDisplayElement('asset.customVideoPoster', this.gallery)) {
      const tenantId = this.gallery['tenant']?.id || this.gallery['tenant'];
      if (this.permissionService.shouldRedirectToPricingPage(tenantId)) {
        this.router.navigate(['/a', 'pricing']);
      }
      return;
    }
    this.asset['tempUploadingPoster'] = true;
    const file = (target as HTMLInputElement).files[0];
    const buffer = await fileToBuffer(file);
    const src = URL.createObjectURL(new Blob([buffer]));

    const aspectRatio = this.asset.version_data.data.width / this.asset.version_data.data.height;

    const resizedImageSrc = await fitImageIntoAspectRatio(src, aspectRatio, 0.9);
    URL.revokeObjectURL(src);

    const blob = await fetch(resizedImageSrc).then(r => r.blob());
    const resizedFile = new File([blob], `image.jpg`, { type: `image/jpeg` });

    const upload = <UploadObj>{
      file: resizedFile,
      status: 'started',
      galleryId: this.gallery.id,
    };

    this.uploadService.uploads.push(upload);
    this.uploadService.markUploadAsUploading(upload);

    this.onClose.emit();

    const assetVersion = await this.assetService.updateAssetVersion(this.asset.version_data.id, {
      poster: {
        content_length: resizedFile.size,
        content_type: resizedFile.type,
        upload_type: 'put',
      },
    });

    await firstValueFrom(this.uploadService.uploadAutoMethod(assetVersion['poster_upload'], upload));

    URL.revokeObjectURL(resizedImageSrc);

    this.uploadService.markUploadAsDone(upload);

    delete this.asset['tempUploadingPoster'];
  }
}
